import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Learn how FindOnlineDegrees collects, uses, protects, and shares personal information when you use our website and services.'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1445" data-mp="2rs40opi912qi">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-1446" data-mp="2e17992026rl1">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1447" data-mp="2dzw2quf8b5q8">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-1448" data-mp="2it8tpb7drom5">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-1449" data-mp="244vwkawk3zq2">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-1450" data-mp="290b30hs8itcg">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-1451" data-mp="1v45z72rne1a8">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1452" data-mp="1z4pzgf7ipl0l">
            <p data-id="element-1453" data-mp="29rmqxa447a00">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-1454" data-mp="2johrdyjy7kgx">
              <li data-id="element-1455" data-mp="2eqpwqwrx5k91">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-1456" data-mp="1r1x0ez7dxadw">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-1457" data-mp="20rgjo5z0f69a">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-1458" data-mp="13basr91als4f">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1459" data-mp="139q7yb4s5njr">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1460" data-mp="1x11sye27ir2y">
              <ul className="list-disc pl-6 space-y-2" data-id="element-1461" data-mp="1wz744onteetl">
                <li data-id="element-1462" data-mp="21t1n1pvliprm">
                  <strong data-id="element-1463" data-mp="21sjscxol0jlt">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-1464" data-mp="1e4if5bggkf02">
                  <strong data-id="element-1465" data-mp="1j1sb79ycrowv">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-1466" data-mp="1nyirnye2kmit">
                  <strong data-id="element-1467" data-mp="2ugnzzoqpr6r5">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-1468" data-mp="nu9kos3kvqn0">
                  <strong data-id="element-1469" data-mp="itlw0d2tnlvy">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-1470" data-mp="52groxoie5fa">
                  <strong data-id="element-1471" data-mp="52yu6c1rlfvn">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-1472" data-mp="3maat78gilj8y">
                  <strong data-id="element-1473" data-mp="3mau80gavbrir">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-1474" data-mp="1kjqzpxogap1d">
                  <strong data-id="element-1475" data-mp="1kj7eo83i7kj4">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-1476" data-mp="1xiqas5ngnvd2">
                  <strong data-id="element-1477" data-mp="1xhygqz9ii5ow">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 12).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-1478" data-mp="19t5u5tes5a6y">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1479" data-mp="19smaa75kkbqa">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-1480" data-mp="1eknrm7hlgsn6">
              <li data-id="element-1481" data-mp="zv85x5wx7dec">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-1482" data-mp="zwj8x3ijkh4e">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-1483" data-mp="14szzs5h4l9l6">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-1484" data-mp="14rgg04w9ggen">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-1485" data-mp="quthvysknk75">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-1486" data-mp="uuk8a1e6ryb5">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-1487" data-mp="c7b7qkw8pxdc">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-1488" data-mp="c7l2smw56upw">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-1489" data-mp="79026mcza90x">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-1490" data-mp="7ab6qoe8zkt3">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-1491" data-mp="l92elt2ioa8s">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-1492" data-mp="g8wju40ya7mh">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-1493" data-mp="v4w1a59y9k4a">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1494" data-mp="v4m6m4dymos9">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1495" data-mp="13yhevladovc4">
              <h3 className="font-bold text-lg" data-id="element-1496" data-mp="13xpi4751ork9">
                Personal information you disclose to us
              </h3>
              <p data-id="element-1497" data-mp="1dwoi71bqvevn">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-1498" data-mp="akzn0olp53pg">
                <strong data-id="element-1499" data-mp="amkhyfbxaonu">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1500" data-mp="kbkhbzuc7m2k">
                <li data-id="element-1501" data-mp="kd59xjx3cuxs">Names</li>
                <li data-id="element-1502" data-mp="kdf5ro4thwbe">Phone numbers</li>
                <li data-id="element-1503" data-mp="pbgjrundz5g0">Email addresses</li>
                <li data-id="element-1504" data-mp="padyc28u1v7h">Mailing addresses</li>
                <li data-id="element-1505" data-mp="u9i7isvrbsxt">Education level and interests</li>
                <li data-id="element-1506" data-mp="u7xamwlyzxws">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1507" data-mp="yb3lkhk12d9l">
                Information automatically collected
              </h3>
              <p data-id="element-1508" data-mp="139wsts8q181b">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1509" data-mp="2xjvag5p6xfdz">
                <li data-id="element-1510" data-mp="2sr21t6qlpmwm">IP address</li>
                <li data-id="element-1511" data-mp="2spzdn6knxyke">Browser and device characteristics</li>
                <li data-id="element-1512" data-mp="2nrxtsjfexv7v">Operating system</li>
                <li data-id="element-1513" data-mp="2nt0iqlt6dnl5">Language preferences</li>
                <li data-id="element-1514" data-mp="2itnym8touum9">Referring URLs</li>
                <li data-id="element-1515" data-mp="2iupagfij3l51">Device name and type</li>
                <li data-id="element-1516" data-mp="2irjld09wgj9b">Pages viewed and time spent</li>
                <li data-id="element-1517" data-mp="3gg3cl0mwpa52">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1518" data-mp="3bg8m2y71mmkj">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1519" data-mp="36ip0fd80n7ik">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1520" data-mp="32glnek5p7uv6">
              <p data-id="element-1521" data-mp="1v4bhmms4di9e">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1522" data-mp="1r0wyvocsqns9">
                <li data-id="element-1523" data-mp="3q6ghd24qpmak">To facilitate account creation and authentication</li>
                <li data-id="element-1524" data-mp="3v61inj7du6qg">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1525" data-mp="3v3yiw7gfgz8j">To respond to user inquiries and offer support</li>
                <li data-id="element-1526" data-mp="3h7lcpuhcgk9d">To send administrative information to you</li>
                <li data-id="element-1527" data-mp="3m4uzf2dcmhti">To fulfill and manage your requests</li>
                <li data-id="element-1528" data-mp="3m7h7d345izc9">To enable user-to-user communications</li>
                <li data-id="element-1529" data-mp="dqmfsjst11dh">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1530" data-mp="hszj71k2wn73">To send you marketing and promotional communications</li>
                <li data-id="element-1531" data-mp="3z8hgfcla7lx">To deliver targeted advertising to you</li>
                <li data-id="element-1532" data-mp="8y1nxntgkycl">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1533" data-mp="wnnly7y3h7j0">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1534" data-mp="1atxw8rv6ewej">To save or protect an individual's vital interest</li>
                <li data-id="element-1535" data-mp="1as4mh3p9nw2b">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1536" data-mp="16ruke24lhpsq">Email Communications</h3>
              <p data-id="element-1537" data-mp="1kk0rkyprsmg1">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-1538" data-mp="1glhx1uabqwss">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1539" data-mp="1bkc4bwgnz9j9">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1540" data-mp="16su5pf245l1g">
              <h3 className="font-bold text-lg" data-id="element-1541" data-mp="11uskanh7t9mt">How We Collect SMS Consent</h3>
              <p data-id="element-1542" data-mp="11soiqc6sgmpp">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1543" data-mp="2daz9cs3tbh3g">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-1544" data-mp="2lolvss0rgrku">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1545" data-mp="2uiqkp5wc3sxq">
                <li data-id="element-1546" data-mp="2ulcqp13ku0du">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-1547" data-mp="22scabjnj4yqt">Follow-up communications related to your inquiry</li>
                <li data-id="element-1548" data-mp="22u6xl4m9x2y2">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1549" data-mp="2br7fqtir24t2">
                Consent Is Not Required
              </h3>
              <p data-id="element-1550" data-mp="2gpir58op0nde">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1551" data-mp="32h1wmx08zvxs">
                Message Frequency and Rates
              </h3>
              <p data-id="element-1552" data-mp="2xlmmog5d2k6n">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1553" data-mp="2jrvm3x9lyan2">How to Opt Out</h3>
              <p data-id="element-1554" data-mp="2jq134ohwcslm">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1555" data-mp="2ti6q1jtakjty">
                <li data-id="element-1556" data-mp="2tjrnbh6zqgvi">Replying STOP to any text message you receive from us</li>
                <li data-id="element-1557" data-mp="2opx5kjay4bz2">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-1558" data-mp="1w2km4i030k9z">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1559" data-mp="gdbj10qj4ztg">For Assistance</h3>
              <p data-id="element-1560" data-mp="1nx83o7b2aby">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1561" data-mp="uc1gihh9h1ic">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-1562" data-mp="z9kstbuef47n">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1563" data-mp="3a3pac40zadwx">Record Retention</h3>
              <p data-id="element-1564" data-mp="3qac06mkbhy5m">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-1565" data-mp="3bijja64vkc1s">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1566" data-mp="d2idhxjboxjj">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1567" data-mp="84gu1bhziw7j">
              <p data-id="element-1568" data-mp="33jfra03imbb">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1569" data-mp="3v8n6wofbfsjm">
                <li data-id="element-1570" data-mp="3v83t9jwp7qbe">
                  <strong data-id="element-1571" data-mp="2nutmt5zn5eyp">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1572" data-mp="zbpgwncfwg5p">
                  <strong data-id="element-1573" data-mp="zc77p4gc2268">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1574" data-mp="18b2g8n6unocp">
                  <strong data-id="element-1575" data-mp="18aja03qkaoei">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1576" data-mp="1i3rprciuas5e">
                  <strong data-id="element-1577" data-mp="1n0qflivh0cxs">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1578" data-mp="1r3n57tjfh9b8">
                  <strong data-id="element-1579" data-mp="1r34yu431xepl">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-1580" data-mp="1mvumkzj6rxe">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1581" data-mp="1ogos9hxo8uq">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1582" data-mp="1505vnjhcg8gr">
              <p data-id="element-1583" data-mp="100t2yi3sk769">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-1584" data-mp="1nvcfbovpseg3">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1585" data-mp="1nuue0du73a1b">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1586" data-mp="1wpqy41fge73y">
              <p data-id="element-1587" data-mp="1so5mskiki0vv">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-1588" data-mp="2q8p15h0mpjsk">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1589" data-mp="2q746zsi882v8">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1590" data-mp="2gahnp85jtjvb">
              <p data-id="element-1591" data-mp="22jw1paksstt7">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-1592" data-mp="1jmbf6a5ia7br">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1593" data-mp="1jltgz7za9j17">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1594" data-mp="241uqj7bivc4k">
              <p data-id="element-1595" data-mp="23zrzql2jpeys">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-1596" data-mp="1gaz0aempsozf">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1597" data-mp="1gb8nxvkaek1y">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1598" data-mp="16em6zhv30d57">
              <p data-id="element-1599" data-mp="16c9hmjmzv0i4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1600" data-mp="xdo1ev4zvxki">
                <li data-id="element-1601" data-mp="xez7x2ptrjfb">The right to access your personal information</li>
                <li data-id="element-1602" data-mp="391klqthk6rnl">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1603" data-mp="3d1bgt9e4btxx">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1604" data-mp="3i2io77ccmr42">The right to opt out of marketing communications</li>
                <li data-id="element-1605" data-mp="3mzr9vhwphda2">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1606" data-mp="2ubljofmvuw2u">
                To exercise these rights, please contact us using the
                information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1607" data-mp="2z9eqnaw0fu46">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-1608" data-mp="26mtzobjg5k15">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1609" data-mp="1oi11it8wx5cm">
                California Residents — Opt Out of Sale/Sharing
              </h3>
              <p data-id="element-1610" data-mp="1tifcok5t8vev">
                California residents — opt out of sale/sharing of personal
                information: To exercise your right to opt out under CCPA/CPRA,
                contact us at the address in Section 12 with the subject line
                "California Privacy Opt-Out." We will process your request
                within 15 business days.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-1611" data-mp="1fhmdvn8h7e5y">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1612" data-mp="1fhcq86awlj3f">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1613" data-mp="i1fhngs2w2h5">
              <p data-id="element-1614" data-mp="n0iewf1th4ek">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-1615" data-mp="2mbk8rm6tvto4">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1616" data-mp="2me6d7cyf9t24">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1617" data-mp="2clqvmgr1f6hr">
              <p data-id="element-1618" data-mp="28kf4u7wfgf7z">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-1619" data-mp="1to68be3x8fz9">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1620" data-mp="1tnoesp8n8fv1">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1621" data-mp="1oylu79voxu8m">
              <p data-id="element-1622" data-mp="1owix65w4f0ue">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-1623" data-mp="1ezwf1ov6inw2">Media Matchers LLC</p>
              <p data-id="element-1624" data-mp="1f295yrix08l9">
                1865 Chestnut Street
                <br data-id="element-1625" data-mp="22r1w27cuvm7y" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-1626" data-mp="27pmxga3ugbxz">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-1627" data-mp="27obolvvjke00">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}